// LivestreamPinnedMessages.jsx
import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getTranslationKey from '../resource/getTranslationKey.js';

import { color } from '../style/variables.js';

import PinBlueIconSource from '../../img/ic-notify.svg';
import IconArrowUpSource from '../../img/ic_arrow_up.svg';

const COLLAPSED_HEIGHT = 36;
const EXPANDED_MAX_HEIGHT = 180;
const WRAPPER_PADDING_TOP = 6;
const COLLAPSE_DELAY = 3000;

export const LivestreamPinnedMessages = ({
  sessionId = null,
  title = '',
  pinnedMessages = EMPTY_ARRAY,
  categories = EMPTY_ARRAY,
  isExpanded = true,
  setIsExpanded = NULL_FUNCTION,
}) => {
  const isAutoExpandRef = useRef(true);
  const collapseTimerRef = useRef(null);

  // Reset auto-expand flag and expand state on session change
  useEffect(() => {
    isAutoExpandRef.current = true;
    setIsExpanded({ sessionId, isExpanded: true });
  }, [setIsExpanded, sessionId]);

  // Auto-collapse after 3 seconds on enter livestream detail
  useEffect(() => {
    if (!isAutoExpandRef.current) return;

    collapseTimerRef.current = setTimeout(() => {
      isAutoExpandRef.current = false;
      setIsExpanded({ sessionId, isExpanded: false });
    }, COLLAPSE_DELAY);

    return () => {
      clearTimeout(collapseTimerRef.current);
    };
  }, [setIsExpanded, sessionId]);

  const toggleExpanded = useCallback(() => {
    if (isAutoExpandRef.current) {
      isAutoExpandRef.current = false;
      clearTimeout(collapseTimerRef.current);
    }

    setIsExpanded({ sessionId, isExpanded: !isExpanded });
  }, [isExpanded, sessionId, setIsExpanded]);

  return (
    <StyledLivestreamPinnedMessages className="swiper-no-swiping">
      {pinnedMessages.length > 0 ? (
        pinnedMessages.map(message => {
          if (!message) return null;
          return (
            <PinnedMessageLayoutItem
              key={message.id}
              isExpanded={isExpanded}
              toggleExpanded={toggleExpanded}
            >
              <PinnedMessageContent
                title={title}
                messageText={message.text ?? ''}
                categories={categories}
                isExpanded={isExpanded}
              />
            </PinnedMessageLayoutItem>
          );
        })
      ) : (
        <PinnedMessageLayoutItem
          isExpanded={isExpanded}
          toggleExpanded={toggleExpanded}
        >
          <PinnedMessageContent
            title={title}
            categories={categories}
            isExpanded={isExpanded}
          />
        </PinnedMessageLayoutItem>
      )}
    </StyledLivestreamPinnedMessages>
  );
};

LivestreamPinnedMessages.propTypes = {
  sessionId: PropTypes.string,
  title: PropTypes.string,
  isExpanded: PropTypes.bool,
  setIsExpanded: PropTypes.func,
  pinnedMessages: PropTypes.array,
  categories: PropTypes.array,
};

const PinnedMessageLayoutItem = ({ children, isExpanded, toggleExpanded }) => {
  return (
    <Wrapper isExpanded={isExpanded} onClick={toggleExpanded}>
      <IconsWrapper>
        <PinnedIcon src={PinBlueIconSource} alt="pinned" />
        <ExpandIcon
          src={IconArrowUpSource}
          alt="expand"
          isExpanded={isExpanded}
        />
      </IconsWrapper>
      <TextWrapper>
        <PinnedMessage isExpanded={isExpanded}>{children}</PinnedMessage>
      </TextWrapper>
    </Wrapper>
  );
};

PinnedMessageLayoutItem.propTypes = {
  children: PropTypes.node,
  isExpanded: PropTypes.bool,
  toggleExpanded: PropTypes.func,
};

const PinnedMessageContent = ({
  title = '',
  messageText = '',
  isExpanded = true,
  categories = EMPTY_ARRAY,
}) => {
  const { t } = useTranslation();
  const hasMessageText = !!messageText;

  const CategoriesComponent = useMemo(() => {
    if (categories.length === 0) return null;

    return (
      <CategoriesWrapper>
        {categories.map(category => (
          <CategoryItem key={category}>
            {t(
              getTranslationKey({
                key: `livestream_settings_tags_${category}`,
              }),
              {
                ns: TranslationNamespace.LIVESTREAM,
              }
            )}
          </CategoryItem>
        ))}
      </CategoriesWrapper>
    );
  }, [categories, t]);

  return (
    <>
      {/* display title timing:
        - expanded: always show
        - collapsed: show if message text is empty */}
      {(isExpanded ? true : !hasMessageText) && (
        <MessageTitle>{title}</MessageTitle>
      )}
      {isExpanded && CategoriesComponent}
      {messageText}
    </>
  );
};

PinnedMessageContent.propTypes = {
  title: PropTypes.string,
  messageText: PropTypes.string,
  isExpanded: PropTypes.bool,
  categories: PropTypes.array,
};

const StyledLivestreamPinnedMessages = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Wrapper = styled.div.attrs(({ isExpanded }) => ({
  style: {
    height: isExpanded ? 'auto' : `${COLLAPSED_HEIGHT}px`,
    maxHeight: isExpanded
      ? `${EXPANDED_MAX_HEIGHT}px`
      : `${COLLAPSED_HEIGHT}px`,
  },
}))`
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding-top: ${WRAPPER_PADDING_TOP}px;
  padding-inline: 28px;
  border-radius: 4px;
  background-color: ${color.blue['100_alpha_40']};
  overflow: hidden;
  transition:
    height 0.4s ease-in-out,
    max-height 0.4s ease-in-out,
    padding-top 0.4s ease-out;
`;

const IconsWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 36px;
  padding: 6px 8px;
  align-items: center;
`;

const PinnedIcon = styled.img`
  position: relative;

  border-radius: 50%;

  width: 16px;
  height: 16px;

  object-fit: contain;
  background-color: ${color.white};
`;

const TextWrapper = styled.div`
  position: relative;
  display: inline-flex;
  min-width: 0;
  flex: 1;
  z-index: 1;
`;

const PinnedMessage = styled.div.attrs(({ isExpanded }) => ({
  style: {
    whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
    maxHeight: isExpanded
      ? `${EXPANDED_MAX_HEIGHT - WRAPPER_PADDING_TOP}px`
      : 'none',
  },
}))`
  padding-top: 3px;
  padding-bottom: 6px;

  text-overflow: ellipsis;
  overflow: hidden auto;
  overscroll-behavior: contain; /* Prevent scroll chaining to parent, which triggers Safari pull-to-refresh */
  &::-webkit-scrollbar {
    display: none;
  }

  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
  color: ${color.neutral['100']};
  overflow-wrap: break-word;
`;

const ExpandIcon = styled.img.attrs(({ isExpanded }) => ({
  style: {
    transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
  },
}))`
  position: relative;

  width: 16px;
  height: 16px;

  object-fit: contain;

  transition: transform 0.4s ease-in-out;
  transform-origin: center;
`;

const MessageTitle = styled.div`
  margin-bottom: 4px;

  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${color.neutral['100']};
`;

const CategoriesWrapper = styled.div`
  white-space: pre-wrap;
`;

const CategoryItem = styled.div`
  margin-right: 4px;
  margin-bottom: 4px;
  border-radius: 40px;
  padding: 2px 8px;

  display: inline-block;

  color: ${color.neutral['100']};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;

  background-color: ${color.neutral['85_alpha_20']};
`;

export default LivestreamPinnedMessages;
